import {
    SelectMultiChoicelist,
    SelectMultiChoicelistOption,
} from 'components/shared/select-multi-choicelist/SelectMultiChoicelist';
import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';

export interface ShippingRateSelectorMarket {
    /** Market GID, e.g. `gid://shopify/Market/123`. */
    id: string;
    name: string;
}

/**
 * Picks the Shopify shipping rates for one country, with an optional shortcut into the Shopify
 * market editor for adding new ones.
 *
 * The unit is a country, not a market: a country can be covered by more than one market, so all of
 * their rates are offered together and the button opens whichever market the merchant picks. With a
 * single market it opens directly; with several it becomes a menu.
 */
export const ShippingRateSelector: React.FC<ShippingRateSelectorProps> = ({
    options,
    values,
    selectedOptions,
    markets = [],
    placeholder,
    addRatesLabel,
    emptyMessage,
    errorMessage,
    hasError,
    hasRemovedRates,
    onChange,
    onRatesUpdated,
}) => {
    const { t } = useTranslation();
    const menuId = useId();

    // The label props stay available as per-caller overrides; these are just the defaults.
    const labels = {
        placeholder: placeholder ?? t('SHIPPING_PROVIDERS.SHIPPING_RATE_SELECTOR.PLACEHOLDER'),
        addRates: addRatesLabel ?? t('SHIPPING_PROVIDERS.SHIPPING_RATE_SELECTOR.ADD_RATES'),
        selectMarket: t('SHIPPING_PROVIDERS.SHIPPING_RATE_SELECTOR.SELECT_MARKET'),
        empty: emptyMessage ?? t('SHIPPING_PROVIDERS.SHIPPING_RATE_SELECTOR.EMPTY'),
        error: errorMessage ?? t('SHIPPING_PROVIDERS.SHIPPING_RATE_SELECTOR.ERROR'),
        removedTitle: t('SHIPPING_PROVIDERS.SHIPPING_RATE_SELECTOR.REMOVED_RATES_BANNER.TITLE'),
        removedDescription: t('SHIPPING_PROVIDERS.SHIPPING_RATE_SELECTOR.REMOVED_RATES_BANNER.DESCRIPTION'),
        refetchDescription: t('SHIPPING_PROVIDERS.SHIPPING_RATE_SELECTOR.REFETCH_BANNER.DESCRIPTION'),
        refetchButton: t('SHIPPING_PROVIDERS.SHIPPING_RATE_SELECTOR.REFETCH_BANNER.BUTTON'),
    };
    const [isOpeningMarket, setIsOpeningMarket] = useState(false);
    // Rates saved in the market editor can take a moment to reach the rates endpoint, so the automatic
    // revalidation right after the editor closes may still miss them — this offers a manual retry.
    const [showRefetchBanner, setShowRefetchBanner] = useState(false);
    const [isRefetching, setIsRefetching] = useState(false);

    const refetchRates = async () => {
        setIsRefetching(true);

        try {
            await onRatesUpdated?.();
        } finally {
            setIsRefetching(false);
            setShowRefetchBanner(false);
        }
    };

    const openMarket = async (marketId: string) => {
        setIsOpeningMarket(true);

        try {
            const activity = await shopify.intents.invoke?.('edit:shopify/Market', { value: marketId });

            const response = await activity?.complete;

            // Rates added in the editor only show up here after a revalidation — only needed when
            // the merchant actually saved the market, not when they closed the editor.
            if (response?.code === 'ok') {
                setShowRefetchBanner(true);
                await onRatesUpdated?.();
            }
        } finally {
            setIsOpeningMarket(false);
        }
    };

    // Kept outside the empty/error branch below: a country with no rates is exactly the case where
    // the merchant needs this button, so it must not disappear along with the selector.
    const addRatesButton = !markets.length ? null : markets.length === 1 ? (
        <s-button icon="plus" loading={isOpeningMarket || undefined} onClick={() => openMarket(markets[0].id)}>
            {labels.addRates}
        </s-button>
    ) : (
        <>
            <s-button icon="plus" commandFor={menuId} loading={isOpeningMarket || undefined}>
                {labels.addRates}
            </s-button>
            <s-menu id={menuId} accessibilityLabel={labels.addRates}>
                <s-section heading={labels.selectMarket}>
                    {markets.map((market) => (
                        <s-button key={market.id} variant="tertiary" onClick={() => openMarket(market.id)}>
                            {market.name}
                        </s-button>
                    ))}
                </s-section>
            </s-menu>
        </>
    );

    return (
        <div className="flex flex-col gap-2">
            <div className="flex gap-2 items-center flex-wrap">
                {/* Full width (and allowed to shrink) on small screens so it can't overflow a narrow card, with
                the add-rates button wrapping below it; from md up it sits next to the button as before */}
                <div className="w-full min-w-0 md:w-auto md:min-w-60 md:flex-1">
                    {/* Still a select when the market has no rates left but some are saved — those
                    are the removed ones, and the merchant has to be able to see and clear them. */}
                    {options.length || values.length ? (
                        <SelectMultiChoicelist
                            options={options}
                            values={values}
                            onChange={onChange}
                            selectedOptions={selectedOptions}
                            placeholder={labels.placeholder}
                        />
                    ) : (
                        <div className="bg-bg-surface-disabled text-text-disabled py-1 px-2 rounded-md">
                            {hasError ? labels.error : labels.empty}
                        </div>
                    )}
                </div>
                {addRatesButton}
            </div>
            {showRefetchBanner && !!onRatesUpdated && (
                <s-banner tone="info" dismissible onDismiss={() => setShowRefetchBanner(false)}>
                    <s-paragraph>{labels.refetchDescription}</s-paragraph>
                    <s-button slot="secondary-actions" loading={isRefetching || undefined} onClick={refetchRates}>
                        {labels.refetchButton}
                    </s-button>
                </s-banner>
            )}
            {hasRemovedRates && (
                <s-banner tone="warning" heading={labels.removedTitle}>
                    <s-paragraph>{labels.removedDescription}</s-paragraph>
                </s-banner>
            )}
        </div>
    );
};

interface ShippingRateSelectorProps {
    options: SelectMultiChoicelistOption<number>[];
    values: number[];
    onChange(values: number[]): void;
    /** Labels for selected ids that aren't in `options` any more. */
    selectedOptions?: SelectMultiChoicelistOption<number>[];
    /** Markets covering this country. Omit to hide the add-rates button. */
    markets?: ShippingRateSelectorMarket[];
    /** Optional overrides; each falls back to its shared translation. */
    placeholder?: string;
    addRatesLabel?: string;
    emptyMessage?: string;
    errorMessage?: string;
    hasError?: boolean;
    /** Some selected rates no longer exist in Shopify; shows a banner asking for a new pick. */
    hasRemovedRates?: boolean;
    /** Called after the market editor closes, to revalidate the available rates. */
    onRatesUpdated?(): void | Promise<unknown>;
}

export default ShippingRateSelector;
